import moment from 'moment';
import i18n from 'i18n';
import _ from 'lodash';
import { numberWithCommas } from 'utils/StringUtil';
import { Order, State } from 'core/order/Order';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';
import { getPercentValue } from 'helper/MetricsHelper';

export type BasicViewDataType = _.Dictionary<{
  value: string | undefined;
  hint?: string | undefined;
}>;
export type PerformanceViewDataType = _.Dictionary<{
  value: string;
  hint?: string;
}>;

export const getBasicViewData = (order: Order): BasicViewDataType => {
  const totalBudget =
    order.state === State.CHANGE_PENDING && order.changeBudget
      ? {
          value: i18n.t<string>('orderDetail.labels.changeBudget', {
            budget: formatPriceWithCurrency(order.currency, order.changeBudget),
          }),
          hint: i18n.t<string>('orderDetail.labels.originBudget', {
            budget: formatPriceWithCurrency(order.currency, order.budget),
          }),
        }
      : { value: formatPriceWithCurrency(order.currency, order.budget) };
  const remainBudget =
    order.state === State.CHANGE_PENDING && order.changeBudget
      ? {
          value: i18n.t<string>('orderDetail.labels.originBudget', {
            budget: formatPriceWithCurrency(
              order.currency,
              order.budgetBalance,
            ),
          }),
          hint: i18n.t<string>('orderDetail.labels.changeBudget', {
            budget: formatPriceWithCurrency(
              order.currency,
              order.budgetBalance + order.changeBudget - order.budget,
            ),
          }),
        }
      : { value: formatPriceWithCurrency(order.currency, order.budgetBalance) };
  const modifyReason =
    order.state === State.CHANGE_PENDING && order.changeBudget
      ? { value: order.modifyReason }
      : { value: undefined };

  return _.omitBy(
    {
      orderNumber: { value: order.orderNumber },
      dateRange: { value: `${order.startDate} ~ ${order.endDate}` },
      remainDays: { value: getRemainDayDesc(order.startDate, order.endDate) },
      totalBudget,
      alreadySpent: {
        value: formatPriceWithCurrency(order.currency, order.spent),
      },
      remainBudget,
      progress: {
        value: getPercentValue(order.spent, order.budget, (value) => value),
      },
      status: { value: getOrderStateDes(order.state) },
      modifyReason,
      creator: { value: order.creator },
      creatorEmail: { value: order.creatorEmail },
    },
    ({ value }) => value === undefined,
  );
};

export const getPerformanceViewData = (
  order: Order,
): PerformanceViewDataType => {
  return {
    impres: { value: numberWithCommas(order.impres) },
    clicks: { value: numberWithCommas(order.clicks) },
    ctr: { value: getPercentValue(order.clicks, order.impres) },
    roas: { value: numberWithCommas((order.roas ?? 0).toFixed(2)) },
  };
};

const getRemainDayDesc = (startDate, endDate) => {
  const now = moment();
  const today = now.startOf('day');
  if (moment(startDate).isAfter(today)) {
    return i18n.t<string>('orderDetail.labels.notstart');
  }

  if (moment(endDate).add(1, 'day').startOf('day').isBefore(now)) {
    return i18n.t<string>('orderDetail.labels.finished');
  }

  if (moment(endDate).startOf('day').isSame(today)) {
    return i18n.t<string>('orderDetail.labels.today');
  }

  return i18n.t<string>('orderDetail.labels.days', {
    days: moment(endDate).diff(today, 'days') + 1,
  });
};

const getOrderStateDes = (state) => {
  if (State.CHANGE_PENDING === state || State.NOT_APPROVE === state) {
    return i18n.t<string>('orderDetail.labels.waitingForReview');
  }
  if (State.REJECT === state) {
    return i18n.t<string>('orderDetail.labels.reject');
  }
  if (State.SETTLE === state) {
    return i18n.t<string>('orderDetail.labels.settle');
  }
  if (State.SETTLED === state) {
    return i18n.t<string>('orderDetail.labels.settled');
  }
  return i18n.t<string>('orderDetail.labels.approve');
};
